import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { useFormReducer, usePopupReducer } from '@tphglobal/common/hooks';
import {
  Button,
  Form,
  FormError,
  FormRow,
  FormRowItem,
  Link,
  MaterialTextInput,
  Modal,
  OutlinedContainer,
} from '@tphglobal/components';
import {
  brand,
  greyScaleColour,
  otherColour,
} from '@tphglobal/common/theme/style.palette';
import { emailValidator, FixedTitle, required } from '@tphglobal/common/utils';
import { fontSize, fontWeight } from '@tphglobal/common/theme/style.typography';
import messages from '../../messages';
import { StyledHeading } from './styles';
import { Disclaimer, PrivacyNotice } from '../../components';

interface Props {
  onSubmit ?: (data: {email : string}) => Promise<unknown>;
  onCancel: () => void;
  email : string
  submittingTimeCard : boolean
}

const validators = {
  email: [required(messages?.general?.validations?.emailRequired), emailValidator],
};

interface ModalPopupMetadata {
  heading: string;
  content: React.ReactElement;
}

const DigitalTimeSheet: React.FC<Props> = ({
  onSubmit,
  onCancel,
  email,
  submittingTimeCard,
}) => {
  const { submitError, handleSubmit, connectField, change } =
    useFormReducer(validators);

  const {
    visibility: tncPopupVisibility,
    showPopup: showTncPopup,
    hidePopup: hideTncPopup,
    metaData: tncPopupMetadata,
  } = usePopupReducer<ModalPopupMetadata>();

  useEffect(() => {
    change('email', email);
  }, []);

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Grid padding="0 24px">
        <FormRow marginBottom="6px">
          <StyledHeading
            fontSize={fontSize?.h5}
            fontWeight={fontWeight?.semiBold}
            lineHeight="21px"
          >
            {messages?.timeCard?.digitalTimesheet?.heading}
          </StyledHeading>
        </FormRow>
        <FormRow marginBottom="24px">
          <FixedTitle varient="Fixed12Regular" color={greyScaleColour?.grey100}>
            {messages?.timeCard?.digitalTimesheet?.subHeading}
          </FixedTitle>
        </FormRow>
        <FormRow minWidth="580px" marginBottom="24px">
          {connectField('email', {
            label: messages?.timeCard?.digitalTimesheet?.email,
          })(MaterialTextInput)}
        </FormRow>
        <FormRow marginBottom='0'>
          <FixedTitle varient='Fixed12Regular' color={greyScaleColour?.grey100}>
            {messages?.timeCard?.digitalTimesheet?.tnc}{' '}
            <Link
              variant='Fixed12Regular'
              color={brand?.secondaryMain}
              onClick={() => {
                showTncPopup({
                  heading: messages?.timeCard?.digitalTimesheet?.disclaimer,
                  content: <Disclaimer />,
                });
              }}
              label={messages?.timeCard?.digitalTimesheet?.disclaimer}
            />{' '}
            {messages?.general?.and}{' '}
            <Link
              variant='Fixed12Regular'
              color={brand?.secondaryMain}
              onClick={() => {
                showTncPopup({
                  heading:
                    messages?.timeCard?.digitalTimesheet
                      ?.privacyCollectionHeading,
                  content: <PrivacyNotice />,
                });
              }}
              label={
                messages?.timeCard?.digitalTimesheet?.privacyCollectionNotice
              }
            />
            {'.'}
          </FixedTitle>
        </FormRow>
        {submitError && (
          <FormRow>
            <FormRowItem>
              <FormError
                message={messages?.departments?.addForm?.failure?.[submitError]}
              />
            </FormRowItem>
          </FormRow>
        )}
      </Grid>
      <OutlinedContainer
        noBorder
        borderRadius="0"
        padding="14px 24px"
        styles={{ backgroundColor: greyScaleColour?.grey60, marginTop: '24px' }}
      >
        <Grid container justifyContent="flex-end">
          <Grid item>
            <Grid container spacing={2}>
              <Grid item>
                <Button
                  variant="outlined"
                  buttonColor={brand?.black}
                  color="primary"
                  size="large"
                  onClick={onCancel}
                  label={messages?.general?.cancel}
                />
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  type="submit"
                  disabled={submittingTimeCard}
                  label={messages?.general?.submit}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </OutlinedContainer>
      <Modal
        show={tncPopupVisibility}
        heading={tncPopupMetadata?.heading}
        onClose={hideTncPopup}
        fitContent
      >
          {tncPopupMetadata?.content}
      </Modal>
    </Form>
  );
};

export default DigitalTimeSheet;
